import { WIRE_OFFSET_DATETIME_PATTERN } from "./wire-patterns";
import { z } from "zod";
import { communityPublicIdentitySchema } from "./community-genesis-v1";
import { communityAddressSchema, referralCodeSchema } from "./community-v1";

/**
 * CODE / Build lane contracts: campaign projects, their imported IMD snapshot and attribution
 * evidence. A participant's full records (pending work, attribution evidence, challenges) are
 * private to them; verified projects also appear in the public showcase with presentation fields
 * and the owner's public identity only.
 *
 * Two ways to prove a project is yours: an IMD project paid from one of your verified wallets, or a
 * website whose domain carries a DNS TXT record we issue. `independent` is the retired GitHub
 * challenge-file method: its records stay readable to their owner, but can't be verified.
 */

const httpsUrlSchema = z.string().url().max(2048).refine((value) => value.startsWith("https://"), {
  message: "Expected an https URL",
});
/** Unicode control (Cc) and format (Cf) characters: invisible text, bidi overrides, zero-width marks. */
const HIDDEN_CHARACTERS = /[\p{Cc}\p{Cf}]/gu;
/** The same, but keeping line breaks and tabs, which an explanation may use. */
const HIDDEN_CHARACTERS_KEEPING_LINES = /(?![\n\t])[\p{Cc}\p{Cf}]/gu;

/** Project names drop hidden characters before validation, in the browser and on the server alike. */
const projectNameSchema = z.string().max(4000)
  .transform((value) => value.replace(HIDDEN_CHARACTERS, ""))
  .pipe(z.string().trim().min(1).max(120));
const integrationSchema = z.string().max(8000)
  .transform((value) => value.replace(HIDDEN_CHARACTERS_KEEPING_LINES, ""))
  .pipe(z.string().trim().min(20).max(1000));
const projectIdSchema = z.guid();
const isoDateSchema = z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN);

export const codeOriginSchema = z.enum(["imd", "website", "independent"]);
export const codeAttributionStatusSchema = z.enum(["pending", "verified", "failed"]);
export const codeAttributionKindSchema = z.enum(["wallet", "domain", "repository"]);

/** The bounded snapshot imported from `GET /jobs/:id`; only documented fields are kept. */
export const codeImdSnapshotSchema = z.object({
  jobId: z.string().min(1).max(128),
  projectId: z.string().min(1).max(128).nullable(),
  versions: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive().nullable(),
  state: z.string().min(1).max(64),
  objective: z.string().max(4000).nullable(),
  paidBy: communityAddressSchema.nullable(),
  repoUrl: httpsUrlSchema.nullable(),
  commit: z.string().max(80).nullable(),
  pullRequestUrl: httpsUrlSchema.nullable(),
  /** A documented site URL only; an ENS name or CID alone is not turned into a link. */
  siteUrl: httpsUrlSchema.nullable(),
  syncedAt: isoDateSchema,
}).strict();

export const codeReviewFailureSchema = z.enum(["repo_not_found", "no_squeekchain_reference"]);

export const codeAttributionSchema = z.object({
  kind: codeAttributionKindSchema,
  status: codeAttributionStatusSchema,
  address: communityAddressSchema.nullable(),
  /** The exact host a website project proves with DNS, e.g. `app.example.com`. */
  domain: z.string().min(1).max(253).nullable(),
  repository: z.string().min(1).max(512).nullable(),
  verifiedAt: isoDateSchema.nullable(),
  /**
   * Why the automatic review failed an IMD project (`failed` only): its delivered repository is
   * gone, or a complete scan of it found no Squeekchain reference. `null` otherwise.
   */
  reviewFailure: codeReviewFailureSchema.nullable(),
  declined: z.object({ reason: z.string().max(500), declinedAt: isoDateSchema }).strict().nullable(),
}).strict();

/** A project cover's LED lattice: 16:9, sampled in the browser from the member's picture. */
export const CODE_COVER_COLS = 64;
export const CODE_COVER_ROWS = 36;
const COVER_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXY";
const COVER_CHARS = (CODE_COVER_COLS * CODE_COVER_ROWS) / 2;

/**
 * A project's headline image as LED levels, never the picture itself: each cell is 0 (unlit) to
 * 4, row-major, two cells per character (`A` + 5a + b). Nothing is uploaded or hosted, and no
 * member-supplied address is ever loaded.
 */
export const codeCoverSchema = z.string().regex(new RegExp(`^[${COVER_ALPHABET}]{${COVER_CHARS}}$`));

/** Levels 0–4 per cell (row-major, `CODE_COVER_COLS` × `CODE_COVER_ROWS`) as a cover string. */
export function encodeCodeCover(levels: Uint8Array): string {
  if (levels.length !== COVER_CHARS * 2) throw new RangeError(`cover needs ${COVER_CHARS * 2} cells`);
  let text = "";
  for (let i = 0; i < levels.length; i += 2) {
    const a = Math.min(4, levels[i]!);
    const b = Math.min(4, levels[i + 1]!);
    text += COVER_ALPHABET[a * 5 + b];
  }
  return text;
}

/** A cover string's levels, row-major; assumes `codeCoverSchema` has validated it. */
export function decodeCodeCover(cover: string): Uint8Array {
  const levels = new Uint8Array(cover.length * 2);
  for (let i = 0; i < cover.length; i += 1) {
    const value = COVER_ALPHABET.indexOf(cover[i]!);
    levels[i * 2] = Math.floor(value / 5);
    levels[i * 2 + 1] = value % 5;
  }
  return levels;
}

/**
 * Set when the Squeekchain team has taken the project down: it's out of the public showcase, no
 * longer counts toward The Buildooooor, and its owner can't edit or verify it. Only the owner sees
 * it; who removed it is never exposed to members.
 */
export const codeProjectRemovalSchema = z.object({
  removedAt: isoDateSchema,
  reason: z.string().min(3).max(500),
}).strict();

/**
 * Set once the Squeekchain team has reviewed and approved the project. Only a verified, approved,
 * not removed project appears publicly (showcase, counts, Genesis feed); until then a verified
 * project is in review and only its owner sees it. Who approved it is never exposed to members.
 */
export const codeProjectApprovalSchema = z.object({
  approvedAt: isoDateSchema,
}).strict();

export const codeProjectSchema = z.object({
  id: projectIdSchema,
  origin: codeOriginSchema,
  externalId: z.string().min(1).max(512),
  name: projectNameSchema,
  integration: integrationSchema,
  /** A website project's address (its host is the verified domain); an IMD project's site. */
  demoUrl: httpsUrlSchema.nullable(),
  customDomain: z.object({
    host: z.string().regex(/^[a-z0-9.-]{3,253}$/),
    status: z.enum(["pending", "verified"]),
    verifiedAt: isoDateSchema.nullable(),
    record: z.string().nullable(),
  }).strict().nullable(),
  repoUrl: httpsUrlSchema.nullable(),
  imd: codeImdSnapshotSchema.nullable(),
  cover: codeCoverSchema.nullable(),
  attribution: codeAttributionSchema,
  syncError: z.string().max(300).nullable(),
  /** `null` for a normal project; see `codeProjectRemovalSchema`. */
  removal: codeProjectRemovalSchema.nullable(),
  /** `null` while in review (or never verified); see `codeProjectApprovalSchema`. */
  approval: codeProjectApprovalSchema.nullable(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
}).strict();

export const codeProjectsResponseSchema = z.object({ projects: z.array(codeProjectSchema) }).strict();

/** Most projects the public showcase returns, newest verification first. */
export const CODE_SHOWCASE_LIMIT = 200;

/**
 * A verified project the Squeekchain team approved, in the public showcase: what it is and who
 * built it. Never wallets,
 * repository-control evidence, person ids, or member-supplied URLs (repositories and demos aren't
 * vetted, so the showcase can't point anyone at them). `owner.code` links to the owner's Genesis
 * profile; `imdJobId` to the job on IMD.
 */
export const codeShowcaseProjectSchema = z.object({
  id: projectIdSchema,
  origin: codeOriginSchema,
  name: projectNameSchema,
  integration: integrationSchema,
  imdJobId: z.string().min(1).max(128).nullable(),
  versions: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive().nullable(),
  buildState: z.string().min(1).max(64).nullable(),
  /** When the member first submitted it. */
  submittedAt: isoDateSchema,
  verifiedAt: isoDateSchema,
  cover: codeCoverSchema.nullable(),
  /**
   * Where to play or visit it: a website's address, whose host the member proved by DNS, or the
   * IMD project's own site. Source links and unverified addresses are never published.
   */
  url: httpsUrlSchema.nullable(),
  owner: z.object({ code: referralCodeSchema, identity: communityPublicIdentitySchema }).strict(),
}).strict();

export const codeShowcaseResponseSchema = z.object({
  projects: z.array(codeShowcaseProjectSchema).max(CODE_SHOWCASE_LIMIT),
}).strict();

export const codeProjectResponseSchema = z.object({ project: codeProjectSchema }).strict();

/**
 * A website project's DNS challenge: publish a TXT record named `name` whose value is `value`.
 * Returned once, when issued; only its hash is stored.
 */
export const codeChallengeSchema = z.object({
  name: z.string().min(1).max(253),
  value: z.string().min(1).max(256),
  expiresAt: isoDateSchema,
}).strict();

export const codeWebsiteSubmitResponseSchema = z.object({
  project: codeProjectSchema,
  /** Absent when an already verified project is edited and needs no new challenge. */
  challenge: codeChallengeSchema.nullable(),
}).strict();

export const codeImdPreviewRequestSchema = z.object({
  job: z.string().trim().min(8).max(2048),
}).strict();

export const codeImdAttributionPreviewSchema = z.enum(["verified", "mismatch", "missing"]);

export const codeImdPreviewResponseSchema = z.object({
  externalId: z.string().min(1).max(512),
  name: projectNameSchema,
  repoUrl: httpsUrlSchema.nullable(),
  demoUrl: httpsUrlSchema.nullable(),
  imd: codeImdSnapshotSchema,
  attribution: codeImdAttributionPreviewSchema,
  /** Payer of the project's root job; ownership follows it, whichever version was pasted. */
  payer: communityAddressSchema.nullable(),
  matchedWallet: communityAddressSchema.nullable(),
  existingProjectId: projectIdSchema.nullable(),
  linkedElsewhere: z.boolean(),
}).strict();

export const codeSubmitImdRequestSchema = z.object({
  job: z.string().trim().min(8).max(2048),
  integration: integrationSchema,
  name: projectNameSchema.optional(),
  cover: codeCoverSchema,
}).strict();

/** Code hosts an IMD delivery's repository link may point to, as `https://<host>/<owner>/<repo>`. */
export const CODE_SOURCE_HOSTS = ["github.com", "gitlab.com", "codeberg.org", "bitbucket.org"] as const;
/**
 * The only host a direct submission's source link may point to: its public repository must carry the
 * project's id (see `CODE_SOURCE_PROOF_FILE`), which the service reads through the GitHub API.
 */
export const DIRECT_SOURCE_HOST = "github.com";
const SOURCE_SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/;

/**
 * Whether `value` links to a repository on a supported code host: `https://<host>/<owner>/<repo>`,
 * optionally deeper (a branch or folder). A bare host such as `https://github.com` is not one.
 */
export function isCodeRepositoryLink(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return false;
  }
  if (url.protocol !== "https:" || url.username !== "" || url.password !== "" || url.port !== "") return false;
  const host = url.hostname.replace(/^www\./, "");
  if (!(CODE_SOURCE_HOSTS as readonly string[]).includes(host)) return false;
  const [owner, repo] = url.pathname.split("/").filter((segment) => segment !== "");
  return owner !== undefined && repo !== undefined && SOURCE_SEGMENT.test(owner) && SOURCE_SEGMENT.test(repo);
}

/** Whether `value` links to a repository on GitHub (`isCodeRepositoryLink`), as a direct submission's source must. */
export function isDirectSourceLink(value: string): boolean {
  return isCodeRepositoryLink(value) && new URL(value.trim()).hostname.replace(/^www\./, "") === DIRECT_SOURCE_HOST;
}

/**
 * A direct submission proves its repository with the project's id in the repository's README, or in
 * this file at its root, on the default branch.
 */
export const CODE_SOURCE_PROOF_FILE = "squeekchain.json";

/** A direct submission (origin `website`): its live site and a required link to its source code. */
export const codeSubmitWebsiteRequestSchema = z.object({
  name: projectNameSchema,
  integration: integrationSchema,
  /** The live app's https address; its exact host is the domain to prove. */
  url: httpsUrlSchema,
  /**
   * The project's public GitHub repository (`isDirectSourceLink`); required, never published. The
   * server answers `invalid_source_link` for anything else. Verification needs the project's id in
   * its README or `CODE_SOURCE_PROOF_FILE`.
   */
  repoUrl: httpsUrlSchema,
  cover: codeCoverSchema,
  /** Cloudflare Turnstile token from the submit form's widget (action `code_submit`). */
  turnstileToken: z.string().min(1).max(2048),
}).strict();

export const codeUpdateProjectRequestSchema = z.object({
  name: projectNameSchema.optional(),
  integration: integrationSchema.optional(),
  /**
   * Direct submissions (origin `website`) only: a replacement source link (`isDirectSourceLink`). It
   * is required, so it can't be cleared; the site address is the identity and can't change.
   */
  repoUrl: httpsUrlSchema.optional(),
  /** A replacement headline image; it is required, so it can't be removed. */
  cover: codeCoverSchema.optional(),
}).strict().refine(
  (value) => value.name !== undefined || value.integration !== undefined || value.repoUrl !== undefined
    || value.cover !== undefined,
  { message: "No editable fields supplied" },
);

/** Why the live-site check of a verification failed (`siteCheck.reason`). */
export const codeSiteCheckReasonSchema = z.enum([
  "unavailable", "unreachable", "not_https", "bad_status", "too_large", "parked", "marker_missing",
]);

/**
 * Why the source check of a verification failed (`sourceCheck.reason`): `unavailable` (GitHub
 * couldn't be read), `unsupported_source` (the link isn't a GitHub repository), `repo_not_found`
 * (missing or private) or `proof_missing` (neither the README nor `CODE_SOURCE_PROOF_FILE` carries
 * the project's id).
 */
export const codeSourceCheckReasonSchema = z.enum(["unavailable", "unsupported_source", "repo_not_found", "proof_missing"]);

/** The page tag proving a site belongs to a project: `<meta name="squeekchain-project" content="<id>">`. */
export const CODE_SITE_MARKER_NAME = "squeekchain-project";

export const codeVerifyResponseSchema = z.object({
  project: codeProjectSchema,
  /**
   * `not_found`: no TXT record at the challenge name; `mismatch`: TXT records exist but none carries
   * the challenge; `claimed_elsewhere`: another participant verified this domain first;
   * `site_check_failed`: the record matches but the live site didn't pass (see `siteCheck`);
   * `source_check_failed`: the record and site pass but the source repository didn't (see
   * `sourceCheck`). After either, the project's status is unchanged.
   */
  result: z.enum(["verified", "not_found", "mismatch", "claimed_elsewhere", "site_check_failed", "source_check_failed"]),
  /**
   * With `site_check_failed`: `unavailable` (the check couldn't run), `unreachable`, `not_https`,
   * `bad_status` (not HTTP 200), `too_large`, `parked` (a parking page) or `marker_missing` (no
   * `squeekchain-project` meta tag with this project's id).
   */
  siteCheck: z.object({ reason: codeSiteCheckReasonSchema }).strict().optional(),
  /** With `source_check_failed`: see `codeSourceCheckReasonSchema`. */
  sourceCheck: z.object({ reason: codeSourceCheckReasonSchema }).strict().optional(),
}).strict();

/**
 * `GET /code/access`: whether the lane is open, and whether this caller may submit and verify
 * projects (always while open; while closed, only an approved preview tester's session).
 */
export const codeAccessResponseSchema = z.object({
  open: z.boolean(),
  canSubmit: z.boolean(),
}).strict();

export type CodeAccessResponse = z.infer<typeof codeAccessResponseSchema>;

export type CodeVerifyResponse = z.infer<typeof codeVerifyResponseSchema>;

export type CodeOrigin = z.infer<typeof codeOriginSchema>;
export type CodeAttributionStatus = z.infer<typeof codeAttributionStatusSchema>;
export type CodeReviewFailure = z.infer<typeof codeReviewFailureSchema>;
export type CodeImdSnapshot = z.infer<typeof codeImdSnapshotSchema>;
export type CodeProjectRemoval = z.infer<typeof codeProjectRemovalSchema>;
export type CodeProjectApproval = z.infer<typeof codeProjectApprovalSchema>;
export type CodeProject = z.infer<typeof codeProjectSchema>;
export type CodeProjectsResponse = z.infer<typeof codeProjectsResponseSchema>;
export type CodeShowcaseProject = z.infer<typeof codeShowcaseProjectSchema>;
export type CodeShowcaseResponse = z.infer<typeof codeShowcaseResponseSchema>;
export type CodeProjectResponse = z.infer<typeof codeProjectResponseSchema>;
export type CodeChallenge = z.infer<typeof codeChallengeSchema>;
export type CodeWebsiteSubmitResponse = z.infer<typeof codeWebsiteSubmitResponseSchema>;
export type CodeImdPreviewResponse = z.infer<typeof codeImdPreviewResponseSchema>;
export type CodeSubmitImdRequest = z.infer<typeof codeSubmitImdRequestSchema>;
export type CodeSubmitWebsiteRequest = z.infer<typeof codeSubmitWebsiteRequestSchema>;
export type CodeUpdateProjectRequest = z.infer<typeof codeUpdateProjectRequestSchema>;
